import type { ClientToServerEvents, Result, ServerToClientEvents } from '@cardengine/shared';
import { io, type Socket } from 'socket.io-client';

export type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

/**
 * Same-origin by default. In dev, Vite proxies /socket.io through to the game
 * server, so this works unchanged in both environments. Set VITE_SERVER_URL
 * only if you split the frontend and the server onto different hosts.
 */
const url = import.meta.env.VITE_SERVER_URL as string | undefined;

export const socket: AppSocket = io(url ?? '/', {
  autoConnect: false,
  transports: ['websocket', 'polling'],
  reconnection: true,
  reconnectionDelay: 500,
  reconnectionDelayMax: 4_000,
  timeout: 10_000,
});

/**
 * Typed wrapper around Socket.IO acknowledgements. Any transport failure is
 * turned into the same Result shape the server uses, so callers only handle one
 * error path.
 */
export async function request<T>(
  event: keyof ClientToServerEvents,
  ...args: unknown[]
): Promise<Result<T>> {
  try {
    const result = (await socket
      .timeout(10_000)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .emitWithAck(event as any, ...(args as any[]))) as Result<T> | undefined;

    return result ?? { ok: false, error: { code: 'INTERNAL', message: 'No response from the server.' } };
  } catch {
    return {
      ok: false,
      error: { code: 'INTERNAL', message: 'The server did not respond. Check your connection.' },
    };
  }
}
